/**
 * PromptStatusBadge component
 * Displays prompt status with appropriate styling
 */
import { Badge } from '@/components/ui/badge'
import type { PromptStatus } from '@/types/prompts'
import { cn } from '@/lib/utils'

interface PromptStatusBadgeProps {
  status: PromptStatus
  /** Optional experimental percentage to show (for experimental status) */
  experimentalPercentage?: number
  className?: string
}

/**
 * Status styling configuration
 */
const STATUS_STYLES: Record<PromptStatus, string> = {
  active: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-100',
  draft: 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-100',
  experimental: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100',
  deprecated: 'bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-100',
}

/**
 * Status display labels
 */
const STATUS_LABELS: Record<PromptStatus, string> = {
  active: 'ACTIVE',
  draft: 'DRAFT',
  experimental: 'EXPERIMENTAL',
  deprecated: 'DEPRECATED',
}

export function PromptStatusBadge({
  status,
  experimentalPercentage,
  className,
}: PromptStatusBadgeProps) {
  const label =
    status === 'experimental' && experimentalPercentage !== undefined
      ? `EXP ${experimentalPercentage}%`
      : STATUS_LABELS[status]

  return (
    <Badge
      variant="outline"
      className={cn(STATUS_STYLES[status], className)}
      data-testid="prompt-status-badge"
    >
      {label}
    </Badge>
  )
}
